import Link from "next/link";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import {
  revenueTiers,
  essentialsFeatures,
  completeFeatures,
  YEARLY_DISCOUNT,
} from "./pricingData";

function formatPrice(price, billingCycle) {
  if (price === null) return null;
  if (billingCycle === "yearly") {
    return Math.round(price * (1 - YEARLY_DISCOUNT));
  }
  return price;
}

export default function PricingCards({ billingCycle, revenueTier }) {
  const tier = revenueTiers[revenueTier];
  const isCustom = tier.essentials === null;
  const essentialsPrice = formatPrice(tier.essentials, billingCycle);
  const completePrice = formatPrice(tier.complete, billingCycle);

  const plans = [
    {
      name: "Essentials",
      description:
        "AI-powered bookkeeping and reporting for businesses that want accurate, real-time financial data.",
      price: essentialsPrice,
      features: essentialsFeatures,
      featured: false,
    },
    {
      name: "Complete",
      description:
        "Full-service accounting with BAS lodgement, tax compliance, and advisory — everything your business needs.",
      price: completePrice,
      features: completeFeatures,
      featured: true,
    },
  ];

  return (
    <section className="py-[80px] px-6 lg:px-10 bg-base-white">
      <div className="max-w-[900px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 fade-in">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-[20px] p-8 sm:p-10 transition-all duration-300 hover:-translate-y-1 ${
                plan.featured
                  ? "bg-base-white border-2 border-blue-primary-500 shadow-[0_8px_32px_rgba(0,116,230,0.12)] hover:shadow-[0_16px_48px_rgba(0,116,230,0.15)]"
                  : "bg-base-white border border-gray-cool-200 hover:shadow-[0_16px_48px_rgba(0,0,0,0.08)]"
              }`}
            >
              {plan.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-primary-500 text-white text-[11px] font-bold px-4 py-1 rounded-full tracking-[1px]">
                  MOST POPULAR
                </div>
              )}

              <h3 className="font-inter text-xl font-extrabold tracking-[-0.5px] text-gray-cool-900 mb-2">
                {plan.name}
              </h3>
              <p className="font-inter text-[13px] text-gray-cool-500 leading-[1.6] mb-6">
                {plan.description}
              </p>

              {/* CTA */}
              <Link
                href="/contact"
                className={`block w-full text-center py-3.5 rounded-full text-[14px] font-semibold transition-all duration-300 mb-8 ${
                  plan.featured
                    ? "bg-blue-primary-500 text-white hover:bg-[#005bb5] hover:shadow-[0_8px_24px_rgba(0,116,230,0.3)]"
                    : "bg-gray-cool-900 text-white hover:bg-gray-cool-800"
                }`}
              >
                {isCustom ? "Contact Us" : "Get Started"}
              </Link>

              {/* Features */}
              <div className="space-y-3">
                <p className="font-inter text-[11px] font-bold uppercase tracking-[1px] text-gray-cool-500 mb-3">
                  What&apos;s included
                </p>
                {plan.features.map((feature) => (
                  <div
                    key={feature}
                    className="flex items-center gap-2.5 text-sm text-gray-cool-700"
                  >
                    <CheckCircleIcon
                      className={`w-5 h-5 flex-shrink-0 ${
                        plan.featured
                          ? "text-blue-primary-500"
                          : "text-green-500"
                      }`}
                    />
                    <span className="font-inter">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
